import qualityEventsHelpers from '../quality-events.helpers';

type AdaptEventExtra = {
    // value of ?variant= query param (entry point A/B variant)
    variant: string;
    // value of ?utm_source= query param (email / banner / order-confirm)
    utm_source: string;
    // value of ?utm_medium= query param (crm / web / transactional)
    utm_medium: string;
    // value of ?utm_campaign= query param (campaign name)
    utm_campaign: string;
};

// The values of LANDING_CTA and CTA_TRIGGER in routes/adapt-landing/adapt-landing.constants.ts. Spelled out
// here because services must not import from routes; a value that drifts from these fails tsc at the call site.
type AdaptLandingCta = 'try_now';
type AdaptWaitlistTrigger = 'click' | 'resume';

// The CTA can leave this tab right after its events: for the Studio, for Typeform on the resume after sign-in, and for
// Typeform when the browser blocks the new tab. keepalive carries the request past the navigation. A copy kept until
// the response would be sent again on the next eduki page load, because the response never reaches a page that has
// left, and one click would be recorded twice. A request that fails while the page is still open is kept all the same
const SENT_BEFORE_LEAVING = { keepCopyUntilResponse: false };

/**
 * Quality Events for the eduki Adapt launch page /de/adapt (AI-1736, AI-1735).
 *
 * Funnel:
 *   pageView → ctaClick (try_now) → [sign in] → Studio (inside eduki_adapt_access_v1) | waitlistRedirect
 *
 * The gap between ctaClick(try_now) and waitlistRedirect is made of teachers who went to the Studio
 * plus logged-out teachers who never finished signing in.
 */
export const ADAPT_LANDING_QUALITY_EVENTS = {
    /**
     * Fired once on component mount, except when the page reopens after sign-in only to resume a click
     * (the teacher was counted before signing in).
     * Provides a baseline traffic counter broken down by source channel (utm_source).
     */
    pageView(extra: AdaptEventExtra) {
        qualityEventsHelpers.track({
            type: 'adapt:pageView',
            extra: JSON.stringify(extra)
        });
    },

    /**
     * Fired on a CTA click. The page has one CTA:
     * cta = 'try_now' → hero "Jetzt ausprobieren": sign in if needed, then Studio or waitlist
     *
     * Uses trackAndForget without the stored copy (SENT_BEFORE_LEAVING), so a click that leaves this tab is
     * neither lost nor recorded twice.
     */
    ctaClick(extra: AdaptEventExtra & { cta: AdaptLandingCta }) {
        qualityEventsHelpers.trackAndForget(
            {
                type: 'adapt:ctaClick',
                extra: JSON.stringify(extra)
            },
            SENT_BEFORE_LEAVING
        );
    },

    /**
     * Fired when a signed-in teacher outside eduki_adapt_access_v1 is sent to the waitlist Typeform.
     * trigger = 'click'  → right after the "Jetzt ausprobieren" click
     * trigger = 'resume' → automatically, after coming back from sign-in
     *
     * Uses trackAndForget without the stored copy (SENT_BEFORE_LEAVING), so a redirect that leaves this tab for
     * Typeform is neither lost nor recorded twice.
     */
    waitlistRedirect(extra: AdaptEventExtra & { trigger: AdaptWaitlistTrigger }) {
        qualityEventsHelpers.trackAndForget(
            {
                type: 'adapt:waitlistRedirect',
                extra: JSON.stringify(extra)
            },
            SENT_BEFORE_LEAVING
        );
    },

    /**
     * Fired once on page leave with the deepest scroll point reached (0–100).
     * Uses trackAndForget (fetch + keepalive) so the event is not lost when
     * the tab closes or the user navigates away.
     */
    scrollDepth(extra: AdaptEventExtra & { depth: number }) {
        qualityEventsHelpers.trackAndForget({
            type: 'adapt:scrollDepth',
            extra: JSON.stringify(extra)
        });
    }
};
